import React from "react";

import {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    Controls,
    MiniMap,
    ReactFlow,
} from "reactflow";
import {CircularProgress,} from "@mui/joy";

// components
import {createGraph, simulateGraph} from "../../utils/d3Force";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import BaseApi from "../../utils/BaseApi";
import DeliveryNode from "./DeliveryNode";


import 'reactflow/dist/style.css';
import DeliveryStageCreate from "./DeliveryStageCreate";
import SelfConnectingEdge from "./SelfConnectingEdge";
import FloatingConnectionLine from "./FloatingConnectionLine";
import Box from "@mui/joy/Box";
import CommonCallBackButton from "../Common/CommonCallBackButton";

// icons
import PolylineIcon from '@mui/icons-material/Polyline';


const nodeTypes = {
    mainNode: DeliveryNode,
};

const edgeTypes = {
    selfconnecting: SelfConnectingEdge,
};


class DeliveryXtypeAdmin extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            edges: null,
            nodes: null,
            isLoading: true,
            highlightedNode: null
        };
    }

    async componentDidMount() {
        await this.retrieveStages();
        await setTimeout(() => {
          this.setState({isLoading: false});
        }, 2000);
    }

    async retrieveStages(){
        const url = `delivery_stage/`;
        const api = new BaseApi();
        const params = {
            is_active: true,
            xtype: this.props.xtype,
        };
        let response = await api.get(url, params);
        let rawNodes = response.data.results.map((stage) => {
            return(
                {
                    id: stage.id,
                    position: {
                        x: stage.x_coordinate,
                        y: stage.y_coordinate,
                    },
                    data: {
                        label: stage.title,
                        obj: stage
                    },
                }
            )
            }
        );
        let rawEdges = [];
        for (let stage of response.data.results) {
            for (let edge of stage.next_stages) {
                rawEdges.push(
                    {
                        id: `${stage.id}-${edge.id}`,
                        source: stage.id,
                        target: edge.id,
                    }
                )
            }
        }
        const {formattedEdges, formattedNodes} = createGraph(
            rawEdges,
            rawNodes,
            this.highlightCallBack.bind(this)
        );
        await this.setState(
            {
                nodes: formattedNodes,
                edges: formattedEdges,
            }
        );
    }

    async simulate(){
        const url = `delivery_stage/`;
        const api = new BaseApi();
        const params = {
            is_active: true,
            xtype: this.props.xtype,
        };
        let response = await api.get(url, params);
        let rawNodes = response.data.results.map((stage) => {
            return(
                {
                    id: stage.id,
                    position: {
                        x: 0,
                        y: 0,
                    },
                    data: {
                        label: stage.title,
                        obj: stage
                    },

                }
            )
            }
        );
        let rawEdges = [];
        for (let stage of response.data.results) {
            for (let edge of stage.next_stages) {
                rawEdges.push(
                    {
                        id: `${stage.id}-${edge.id}`,
                        source: stage.id,
                        target: edge.id,
                    }
                )
            }
        }
        const {formattedEdges, formattedNodes} = simulateGraph(
            rawEdges,
            rawNodes,
            this.highlightCallBack.bind(this)
        );
        await this.setState(
            {
                nodes: formattedNodes,
                edges: formattedEdges,
            }
        );
        for (const node of formattedNodes){
            const url = `delivery_stage/${node.id}/`;
            const api = new BaseApi();
            const data = {
                x_coordinate: Math.ceil(node.position.x),
                y_coordinate: Math.ceil(node.position.y),
            }
            await api.patch(url, data);
        }
    }

    highlightCallBack(id){
        if (id === this.state.highlightedNode){
            this.setState(
            {
                highlightedNode: null,
                edges: this.state.edges.map((edge) => {
                            edge.style = {stroke: 'white'}
                            return edge
                        }
                    )
                }
            );
        }
        else {
            let edges = this.state.edges.map((edge) => {
                if (edge.source === id.toString()){
                    edge.style = {stroke: 'yellow'}
                }
                else if (edge.target === id.toString()){
                    edge.style = {stroke: 'orange'}
                }
                else {
                    edge.style = {stroke: 'white'}
                }
                return edge
            });
            this.setState(
                {
                    highlightedNode: id,
                    edges: edges,
                }
            );
        }
    }

    async onNodesChange(changes) {
        if (changes){
            for (const change of changes){
                if (change.type === 'position' && change.dragging){
                    const url = `delivery_stage/${change.id}/`;
                    const api = new BaseApi();
                    const data = {
                        x_coordinate: Math.ceil(change.position.x),
                        y_coordinate: Math.ceil(change.position.y),
                    }
                    await api.patch(url, data);
                }
            }
        }
        this.setState(
            {
                nodes: applyNodeChanges(changes, this.state.nodes)
            }
        );
    }

    onEdgesChange(changes) {
        this.setState(
            {
                edges: applyEdgeChanges(changes, this.state.edges)
            }
        );
    }

    async onConnect(params) {
        this.setState(
            {
                edges: addEdge(params, this.state.edges)
            }
        );
        const url = `delivery_stage/${params.source}/`;
        const api = new BaseApi();
        let response = await api.get(url, {});
        let next_stages = response.data.next_stages.map(each => {
            return each.id
        })
        next_stages.push(parseInt(params.target))
        await api.patch(
            url,
            {
                next_stages: next_stages
            }
        );
        // await this.retrieveStages();
    }

    render() {
        if (!this.state.nodes || !this.state.edges || this.state.isLoading){
            return (<CircularProgress/>)
        }
        return(
            <>
                <Box
                    sx={{
                        mt: 1,
                        mb: 1
                    }}
                >
                    <DeliveryStageCreate
                        xtype={this.props.xtype}
                        callBack={this.retrieveStages.bind(this)}
                    />
                    <CommonCallBackButton
                        icon={PolylineIcon}
                        label={'Распределить'}
                        color={'neutral'}
                        callBack={this.simulate.bind(this)}
                    />
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        height: 800,
                        border: 2,
                        borderColor: '#FFF',
                    }}
                >
                    <ReactFlow
                        nodes={this.state.nodes}
                        edges={this.state.edges}
                        onNodesChange={this.onNodesChange.bind(this)}
                        onEdgesChange={this.onEdgesChange.bind(this)}
                        connectionLineComponent={FloatingConnectionLine}
                        //fitView
                        defaultViewport={{
                            x: 0,
                            y: 0,
                            zoom: 0.1
                        }}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onConnect={this.onConnect.bind(this)}
                    >
                        <Background />
                        <MiniMap
                            pannable={true}
                        />
                        <Controls/>
                    </ReactFlow>
                </Box>
            </>
        )
    }
}

export default DeliveryXtypeAdmin;
